import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { api } from "@/lib/api";

/**
 * TopupConfirmed — dedicated confirmation page shown after a client
 * successfully books their Anti-Wrinkle top-up. Different from the
 * standard treatment booking success page so we can:
 *  - Confirm it's a top-up (30 min, Anti-Wrinkle – Top Up)
 *  - Skip payment / consent messaging entirely
 */
export default function TopupConfirmed() {
  const [sp] = useSearchParams();
  const bid = sp.get("bid") || "";
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!bid) { setError("Missing booking id"); return; }
    api.get(`/bookings/${bid}/public`).then((r) => setB(r.data))
      .catch(() => setError("Could not load your booking"));
  }, [bid]);

  return (
    <>
      <Header />
      <div className="max-w-2xl mx-auto p-6 lg:p-12" data-testid="topup-confirmed">
        {error && <p className="text-red-600">{error}</p>}
        {!b && !error && <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>}
        {b && (
          <>
            <div className="flex items-center gap-3">
              <CheckCircle2 size={32} className="text-emerald-700" />
              <p className="mono-label text-emerald-700">TOP-UP CONFIRMED</p>
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl mt-6 leading-tight">
              Your Anti-Wrinkle top-up has been successfully booked.
            </h1>
            <p className="text-stone-600 mt-4 leading-relaxed">
              Hi {(b.client_name || "there").split(" ")[0]}, your complimentary review appointment is locked in. We look forward to seeing you at CLAÈ Aesthetics.
            </p>

            <div className="mt-10 border hairline bg-white">
              <div className="px-6 py-4 border-b hairline bg-alt">
                <span className="mono-label">Appointment</span>
              </div>
              <div className="px-6 py-5 space-y-3">
                <Row label="Appointment" value="Anti-Wrinkle – Top Up" testid="topup-appt-name" />
                <Row label="Date" value={fmtDate(b.date)} testid="topup-appt-date" />
                <Row label="Time" value={b.time} testid="topup-appt-time" />
                <Row label="Duration" value="30 minutes" testid="topup-appt-duration" />
                <Row label="Reference" value={b.reference} mono testid="topup-appt-ref" />
              </div>
            </div>

            <div className="mt-8 border hairline bg-white px-6 py-4 text-sm text-stone-600 leading-relaxed">
              No payment or consent form is required — your Anti-Wrinkle top-up is included with your original treatment.
              If you need to reschedule or cancel, please contact CLAÈ Aesthetics directly.
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/" data-testid="topup-return-home" className="border border-stone-900 px-6 py-3 mono-label hover:bg-stone-900 hover:text-white transition">Return to CLAÈ</Link>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="mono-label text-stone-500">{label}</span>
      <span data-testid={testid} className={mono ? "font-mono text-sm" : "font-serif text-lg"}>{value}</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
